import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import AppValues from '../common/app.values';
import FieldsConstants from '../common/fields.constants';
import { FieldCustomValidators } from '../forms/fields/field-custom.validators';

@Injectable()
export class ValidationService {
  private readonly passwordBase: ValidatorFn[] = [
    Validators.required,
    Validators.minLength(FieldsConstants.MIN_PASSWORD_LEN),
    Validators.maxLength(FieldsConstants.MAX_PASSWORD_LEN),
    FieldCustomValidators.checkWhitespaceAndRepeatedCharacters(AppValues.whitespacesPattern),
    Validators.pattern(AppValues.repeatedCharactersPattern),
    Validators.pattern(AppValues.numberPattern),
  ];

  private readonly validationFields: {
    [key: string]: ValidatorFn[];
  } = {
    email: [
      Validators.required,
      Validators.pattern(AppValues.emailPattern),
      Validators.minLength(FieldsConstants.MIN_EMAIL_LEN),
    ],
    accountType: [Validators.required],
    alias: [
      Validators.minLength(FieldsConstants.MIN_ALIAS_LEN),
      Validators.maxLength(FieldsConstants.MAX_ALIAS_LEN),
      Validators.pattern(AppValues.aliasPattern),
    ],
    organizationName: [
      Validators.minLength(FieldsConstants.MIN_ORGANIZATION_NAME_LEN),
      Validators.maxLength(FieldsConstants.MAX_ORGANIZATION_NAME_LEN),
      Validators.pattern(AppValues.organizationNamePattern),
    ],
    oldPassword: [Validators.required, Validators.maxLength(FieldsConstants.MAX_PASSWORD_LEN)],
    password: [
      ...this.passwordBase,
      Validators.pattern(AppValues.lettersPattern),
      Validators.pattern(AppValues.symbolsPattern),
    ],
    repeatPassword: [Validators.required],
    noWhiteSpacesOnly: [Validators.pattern(AppValues.whiteSpacesOnlyPattern)],
  };

  private validationMessages: { [key: string]: string } = {
    email: 'FIELD_EMAIL_ERROR',
    accountType: 'FIELD_ACCOUNT_TYPE_ERROR',
    alias: 'FIELD_ALIAS_ERROR',
    organizationName: 'FIELD_ORGANIZATION_NAME_ERROR',
    password: 'FIELD_PASSWORD_ERROR',
    repeatPassword: 'REPEAT_PASSWORD_INPUT_ERROR',
    currentPassword: 'REPEAT_PASSWORD_INPUT_ERROR',
    incorrectPassword: 'INCORRECT_PASSWORD_INPUT_ERROR',
    noWhiteSpacesOnly: 'NO_WHITE_SPACES_ONLY',
    valid: 'VALID_LOOKS_GOOD',
  };

  private fieldsMaxInputLength: { [key: string]: number } = {
    email: FieldsConstants.MAX_EMAIL_LEN,
    alias: FieldsConstants.MAX_ALIAS_LEN,
    organizationName: FieldsConstants.MAX_ORGANIZATION_NAME_LEN,
  };

  public getValidators(controlName: string): ValidatorFn[] {
    return this.validationFields[controlName] || [];
  }

  public getRequiredIfSelectionEqualsToValidator(enableControlName: string, enabledValue: string): ValidatorFn {
    return FieldCustomValidators.requiredIfSelectionEqualsTo(enableControlName, enabledValue);
  }

  public getRequiredValidator(): ValidatorFn {
    return Validators.required;
  }

  public getFormPasswordValidators(): ValidationErrors | null {
    return FieldCustomValidators.passwordMatches;
  }

  public getErrorDescriptionId(type: string): string {
    return this.validationMessages[type] || '';
  }

  public setMaxInputLength(formControl: AbstractControl, controlName: string): void {
    const maxLength: number = this.fieldsMaxInputLength[controlName];
    if (maxLength !== undefined) {
      this.trackInputMaxLength(formControl.get(controlName), maxLength);
    }
  }

  public isFieldInvalid(form: AbstractControl, controlName: string): boolean {
    const control: AbstractControl = form.get(controlName);
    const isInvalid: boolean = (control.invalid && control.touched) || control.hasError('minlength');
    switch (controlName) {
      case 'email':
        return isInvalid || control.hasError('email');
      case 'accountType':
        return isInvalid;
      case 'alias':
        return isInvalid || control.hasError('pattern');
      case 'organizationName':
        return isInvalid || control.hasError('pattern');
      case 'password':
        return (
          isInvalid ||
          control.hasError('pattern') ||
          control.hasError('findPasswordRegEx') ||
          control.hasError('maxlength')
        );
      case 'repeatPassword':
        return (control.invalid && control.touched) || (control.touched && form.hasError('passwordsNotMatch'));
      case 'currentPassword':
        return isInvalid || control.hasError('maxlength');
      case 'agreeCheck':
        return isInvalid;
      default:
        return false;
    }
  }

  /**
   * @description Subscribes on the specified control changes. Cuts off the control value if it exceeds
   *              maxLength without emitting events with the latest value when the control value is updated.
   * @param {AbstractControl} control The specified control
   * @param {number} maxLength The specified control value max length
   */
  private trackInputMaxLength(control: AbstractControl, maxLength: number): void {
    control.valueChanges.subscribe(() => {
      if ((control.value as string).length > maxLength) {
        control.setValue((control.value as string).substring(0, maxLength), {
          emitEvent: false,
        });
      }
    });
  }
}
